/**
 * One number formatter, shared by every renderer and handed to generated code.
 *
 * Until this existed, each generated component wrote its own. Some wrote a good
 * one — the treemap in an earlier build carried a private `fmtCompact` with
 * B/M/K tiers — and some wrote none, which is how a live board's treemap
 * labelled its cells **3205473599.79255** and **1135541307.0932** while the
 * Power BI boards it is measured against show `3.21B` everywhere, on every
 * axis, bar end and donut label.
 *
 * Asking a model to format numbers correctly in every component it writes is a
 * bet that never fully pays. Giving it a formatter in scope is a capability, so
 * `fmt` is added to the live scope and the fallback renderer uses it too. What
 * a component chooses to display is still its own decision; it no longer has to
 * implement the arithmetic to make that decision well.
 */

/** Compact magnitude with a unit suffix: 3.21B, 1.14B, 932.3M, 41.9K, 871. */
export function compact(value: unknown, digits = 1): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  // One decimal at every tier. An earlier draft dropped it above 100 to save
  // width, which was an invention: the reference boards print "163.25K",
  // "105.97K" and "3.30K" at two decimals well past 100, and the saving is a
  // single character — a compact number tops out at "999.9M", six characters.
  // Precision the reader can use beats a column of width nobody needed.
  const tier = (div: number, suffix: string) =>
    `${trimZeros((n / div).toFixed(digits))}${suffix}`;
  if (abs >= 1e12) return tier(1e12, 'T');
  if (abs >= 1e9) return tier(1e9, 'B');
  if (abs >= 1e6) return tier(1e6, 'M');
  if (abs >= 1e3) return tier(1e3, 'K');
  // Below a thousand, show what is there but never a spurious ".0" — a live
  // board read "871.0" for a whole number of units.
  return trimZeros(n.toFixed(Math.abs(n) < 1 && n !== 0 ? 2 : dpFor(n)));
}

function dpFor(n: number): number {
  return Number.isInteger(n) ? 0 : 1;
}

function trimZeros(s: string): string {
  const trimmed = s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  // U+2212 MINUS SIGN, not a hyphen. The renderer this replaced already chose
  // it for displayed numbers and it is the typographically correct glyph; the
  // hyphen a `toFixed` produces is narrower than the digits it sits beside.
  return trimmed.replace(/^-/, '\u2212');
}

export type ValueFormat = 'number' | 'compact' | 'percent' | 'currency' | 'integer';

/**
 * Format one value for display.
 *
 * `percent` takes PERCENT POINTS (33.2 -> "33.2%"), matching what the engine
 * serves for a rate: see `_rate.pct` in proxy.py. Passing a 0..1 fraction here
 * is the mistake that renders a 19% share as "0.2%".
 */
export function formatValue(
  value: unknown,
  opts: { format?: ValueFormat; currency?: string; digits?: number; unit?: string } = {},
): string {
  const n = Number(value);
  if (value === null || value === undefined || value === '') return '';
  if (!Number.isFinite(n)) return String(value);
  const { format = 'compact', currency = '$', digits = 1, unit } = opts;
  let out: string;
  switch (format) {
    case 'percent':
      out = `${trimZeros(n.toFixed(Math.abs(n) < 10 ? digits : 0))}%`;
      break;
    case 'currency':
      out = `${currency}${compact(n, digits)}`;
      break;
    case 'integer':
      out = Math.round(n).toLocaleString();
      break;
    case 'number':
      out = n.toLocaleString(undefined, { maximumFractionDigits: 2 });
      break;
    default:
      out = compact(n, digits);
  }
  return unit ? `${out} ${unit}` : out;
}

/** The tiers `compact` chooses between, largest first. */
const TIERS: Array<{ div: number; suffix: string }> = [
  { div: 1e12, suffix: 'T' },
  { div: 1e9, suffix: 'B' },
  { div: 1e6, suffix: 'M' },
  { div: 1e3, suffix: 'K' },
  { div: 1, suffix: '' },
];

/**
 * ONE unit for a whole series.
 *
 * `compact` picks its tier from the magnitude of a SINGLE value, which is right
 * for a standalone number and wrong for every axis, bar label and legend on one
 * chart: each label chose its own unit, so a series straddling a boundary
 * rendered "980K" next to "1.1M" on the same axis, and two tiles bound to the
 * same measure over the same range disagreed about whether it was a K or an M
 * quantity — a live board printed 262.0K..504.5K on one tile and $14M..$48M on
 * another (AG-2035). A reader cannot compare those.
 *
 * The tier comes from the series' largest magnitude, so every value is stated
 * against the same unit and the suffix can be said once — on the axis title, or
 * as the scale note — instead of on every label.
 */
export function compactScale(values: Iterable<unknown>, digits = 1) {
  let max = 0;
  for (const v of values) {
    const n = Math.abs(Number(v));
    if (Number.isFinite(n) && n > max) max = n;
  }
  const tier = TIERS.find((t) => max >= t.div) ?? TIERS[TIERS.length - 1];
  return {
    /** The suffix every value in this series carries. '' below a thousand. */
    suffix: tier.suffix,
    /** One value, in the series' unit. */
    format(value: unknown): string {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      // Below the thousand tier there is no division to do, and a whole number
      // must not grow a spurious ".0".
      if (tier.div === 1) return trimZeros(n.toFixed(Number.isInteger(n) ? 0 : digits));
      return `${trimZeros((n / tier.div).toFixed(digits))}${tier.suffix}`;
    },
  };
}

/** What generated components receive in scope as `fmt`. */
export const fmt = { compact, value: formatValue, scale: compactScale };
